import { useAppUtils } from '@/composables/useAppUtils';
import api from '@/services/axios';
import { createBaseStore } from '@/stores/api/base.js';
import { useAppStore } from '@/stores/appStore';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useAssetStore = defineStore('asset', () => {
  const appStore = useAppStore();
  const currentAsset = ref(null);
  const { formatUrlParams } = useAppUtils();

  const {
    pagination,
    items: assets,
    fetchItems: fetch,
    storeItem: store,
    updateItem: update,
    destroy,
  } = createBaseStore('api/assets/asset');

  const fetchForWorkspace = async (workspace, params = {}) => {
    const urlParams = formatUrlParams({ workspace, ...params });
    const overrideUrl = `/api/assets/asset?${urlParams}`;
    return await fetch(null, overrideUrl);
  };

  const fetchForFolder = async (folder, params = {}) => {
    const urlParams = formatUrlParams({ folder, params });
    const overrideUrl = `/api/assets/asset?${urlParams}`;
    return await fetch(null, overrideUrl);
  };

  /**
   * Upload a file with unknown type using multi-step process:
   * 1. Request upload URL
   * 2. Upload file (to S3 or local storage)
   * 3. Complete the upload
   */
  const uploadFile = async (file, additionalData = {}) => {
    try {
      appStore.setLoading(true);
      // Extract extension from filename
      const extension = file.name.split('.').pop().toLowerCase();
      const nameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

      // Step 1: Request upload URL
      const requestPayload = {
        name: nameWithoutExtension,
        filename: file.name,
        extension: extension,
        content_type: file.type || 'application/octet-stream',
        folder: currentAsset.value?.folder, // Required: folder from current asset
        ...additionalData,
      };

      const requestResponse = await api.post('/api/assets/asset/uploads/request/', requestPayload);

      const { version, upload_url, headers: uploadHeaders } = requestResponse.data;
      const versionCode = version?.code;

      if (!versionCode) {
        throw new Error('Upload request failed - no version code returned');
      }

      // Step 2: Upload the file to the provided URL
      // Send the file directly as binary data, not as FormData
      // Use plain axios (not api instance) to avoid sending auth headers
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...uploadHeaders,
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          appStore.setProgress(percent);
        },
      });

      // Step 3: Complete the upload using version code
      const completeResponse = await api.post(`/api/assets/asset/uploads/complete/${versionCode}/`);

      return completeResponse.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      appStore.setLoading(false);
      appStore.setProgress(0);
    }
  };

  return {
    pagination,
    assets,
    currentAsset,
    fetch,
    destroy,
    update,
    store,
    fetchForWorkspace,
    fetchForFolder,
    uploadFile,
  };
});
